import '@fontsource-variable/inter'
import '../components/App.css'

import { ApolloProvider } from '@apollo/client'
import { ChakraProvider } from '@chakra-ui/react'
import { browserTracingIntegration } from '@sentry/browser'
import * as Sentry from '@sentry/react'
import { extend } from 'dayjs'
import isBetween from 'dayjs/plugin/isBetween'
import { AppProps } from 'next/app'
import { useRef } from 'react'

import { AdminWrapper } from '~/components/AdminWrapper'
import { AuthWrapper } from '~/components/AuthWrapper'
import { ErrorBoundary } from '~/components/ErrorBoundary'
import { FirmWrapper } from '~/components/FirmWrapper'
import { FrigadeAssistantWrapper } from '~/components/FrigadeAssistantWrapper'
import { FrigadeEngageWrapper } from '~/components/FrigadeEngageWrapper'
import { LogRocketWrapper } from '~/components/LogRocketWrapper'
import { PylonWrapper } from '~/components/PylonWrapper'
import { apolloClient, ToastComponent } from '~/data/apollo'
import { theme } from '~/theme/theme'
import { Z_INDEX_PORTAL } from '~/theme/zIndex'
import 'react-day-picker/dist/style.css'
import '~/util/agGrid'

extend(isBetween)

/** Extracts the GraphQL operation name from "operationName: error message" format */
function extractGraphQLOperation(message: string): string | null {
  const match = /^([a-zA-Z_][a-zA-Z0-9_]*):\s/.exec(message)
  return match?.[1] ?? null
}

Sentry.init({
  dsn: 'https://66ee286b107c4231b96275d756c12531@o4504469882470400.ingest.sentry.io/4504469888958464',
  integrations: [browserTracingIntegration()],
  tracesSampleRate: 0,
  environment: process.env.NEXT_PUBLIC_ENVIRONMENT,
  beforeSend(event, hint) {
    const error = hint.originalException
    const errorName = error instanceof Error ? error.name : String(error)
    const errorMessage = error instanceof Error ? error.message : String(error)

    const isApolloError =
      errorName === 'ApolloError' ||
      (error instanceof Error && error.stack?.includes('@apollo/client'))

    const operation = extractGraphQLOperation(errorMessage)
    const hasOperationSyntax = operation !== null

    if (isApolloError && hasOperationSyntax) {
      const errorType = isApolloError ? 'ApolloError' : 'GraphQLError'
      event.fingerprint = [errorType, operation ?? 'unknown', errorMessage]
    }

    return event
  },
})

function MyApp({ Component, pageProps }: AppProps) {
  // This is a hack (I wrote this not AI) to force the toast container
  // to be rendered above the modal by putting it at the bottom of the DOM
  // This does have the effect of also placing our toasts above menus and everything else
  // anything in a portal will display below the toast ref.
  const toastContainerRef = useRef<HTMLDivElement>(null)
  return (
    <ChakraProvider
      resetCSS
      theme={theme}
      cssVarsRoot="body"
      portalZIndex={Z_INDEX_PORTAL}
      toastOptions={{
        portalProps: {
          containerRef: toastContainerRef,
        },
      }}
    >
      <ApolloProvider client={apolloClient}>
        <ErrorBoundary>
          <AuthWrapper>
            <FirmWrapper>
              <AdminWrapper>
                <LogRocketWrapper>
                  <PylonWrapper>
                    <FrigadeEngageWrapper>
                      <FrigadeAssistantWrapper>
                        <Component {...pageProps} />
                        <ToastComponent />
                        <div ref={toastContainerRef} />
                      </FrigadeAssistantWrapper>
                    </FrigadeEngageWrapper>
                  </PylonWrapper>
                </LogRocketWrapper>
              </AdminWrapper>
            </FirmWrapper>
          </AuthWrapper>
        </ErrorBoundary>
      </ApolloProvider>
    </ChakraProvider>
  )
}

export default MyApp
